import { BaseService } from "../service/BaseService";
import { getStringFromStorage } from "./storagehelper";
import { ProfileSettings } from "../types/app.storage.enums";

const reCalculate = async () => {
  try {
    Excel.run(async (context) => {
      // context.application.calculate(Excel.CalculationType.fullRebuild);
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.getUsedRange().calculate();
      await context.sync();
    });
  } catch (error) {
    console.log("Error: " + error);
  }
};
const hashNameCalculate = async () => {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const usedRange = sheet.getUsedRange();
      usedRange.load(["values", "formulas", "rowCount", "columnCount"]);
      await context.sync();

      if (!usedRange || !usedRange.values || !usedRange.formulas || usedRange.rowCount === 0 || usedRange.columnCount === 0) {
        console.log("No data found in the active sheet");
        return;
      }

      const values = usedRange.values;
      const formulas = usedRange.formulas;
      const changes = [];
      for (let i = 0; i < values.length; i++) {
        for (let j = 0; j < values[i].length; j++) {
          if (values[i][j] !== "#NAME?") {
            continue;
          }

          const formula = formulas[i][j];
          if (!isExcelFormula(formula)) {
            continue;
          }

          const newFormula = formula.split("_xldudf_").join("");
          changes.push({ row: i, col: j, formula: newFormula });
          console.log(`Found #NAME? formula to rewrite: ${formula} -> ${newFormula}`);
        }
      }

      if (changes.length === 0) {
        console.log("No #NAME? custom function formulas were found");
        return;
      }

      changes.forEach((change) => {
        const cell = usedRange.getCell(change.row, change.col);
        cell.clear(Excel.ClearApplyTo.contents);
        cell.formulas = [[change.formula]];
      });
      await context.sync();
      console.log(`Successfully rewritten ${changes.length} #NAME? formula(s)`);
    });
  } catch (error) {
    console.error("Error fixing #NAME? formulas: " + error);
    throw error;
  }
};

const writeToSheet = async (headers: any[], data: any[]) => {
  const matrix = [...(headers || []), ...(data || [])].map((row) =>
    (row || []).map((cell) => (cell === undefined || cell === null ? "" : cell))
  );

  if (matrix.length === 0 || !Array.isArray(matrix[0]) || matrix[0].length === 0) {
    throw new Error("No data available to write to the sheet.");
  }

  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const activeCell = context.workbook.getActiveCell();
      activeCell.load(["rowIndex", "columnIndex"]);
      await context.sync();

      const range = sheet.getRangeByIndexes(
        activeCell.rowIndex,
        activeCell.columnIndex,
        matrix.length,
        matrix[0].length
      );
      range.values = matrix;
      range.getRow(0).format.font.bold = true;
      range.format.autofitColumns();
      await context.sync();
    });
  } catch (error) {
    await new Promise((resolve, reject) => {
      Office.context.document.setSelectedDataAsync(
        matrix,
        { coercionType: Office.CoercionType.Matrix },
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            reject(new Error(asyncResult.error?.message || String(error)));
          } else {
            resolve(undefined);
          }
        }
      );
    });
  }
};

const activeSheetName = async function getActiveWorksheet() {
  let sheetName = "";
  await Excel.run(async function (context) {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    sheet.load("name");
    await context.sync().then(function () {
      sheetName = sheet.name;
    });
  }).catch(function (error) {
    console.log("Error: " + error);
  });
  return sheetName;
};

const activeSheetProps = async () => {
  const props = { id: "", name: "" };
  await Excel.run(async function (context) {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    sheet.load("name");
    await context.sync().then(function () {
      props.name = sheet.name;
      props.id = sheet.id;
    });
  }).catch(function (error) {
    console.log("Error: " + error);
  });
  return props;
};

const getAllWorkSheets = async () => {
  let sheets = [];
  await Excel.run(async (context) => {
    const workbook = context.workbook;
    const worksheets = workbook.worksheets;
    worksheets.load("items");
    await context.sync().then(() => {
      sheets = worksheets.items.map((item) => {
        return { id: item.id, name: item.name };
      });
    });
  }).catch(function (error) {
    console.log("Error: " + error);
  });
  return sheets;
};

const BURGISS_TO_PRIVATEI: Array<[string, string]> = [
  ["Burgiss", "Privatei"],
  ["burgiss", "privatei"],
  ["BURGISS", "PRIVATEI"],
];

const PRIVATEI_TO_BURGISS: Array<[string, string]> = [
  ["Privatei", "Burgiss"],
  ["privatei", "burgiss"],
  ["PRIVATEI", "BURGISS"],
];

const isExcelFormula = (value: unknown): value is string => {
  if (typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  return trimmed.startsWith("=") || trimmed.startsWith("{=");
};

const applyNamespaceReplacements = (formula: string, replacements: Array<[string, string]>): string => {
  let updated = formula;
  for (const [from, to] of replacements) {
    if (updated.includes(from)) {
      updated = updated.split(from).join(to);
    }
  }
  return updated;
};

const renameFormulasInActiveSheet = async (replacements: Array<[string, string]>, successLabel: string) => {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const usedRange = sheet.getUsedRange();

      usedRange.load(["formulas", "rowCount", "columnCount"]);
      await context.sync();

      if (!usedRange || !usedRange.formulas || usedRange.rowCount === 0 || usedRange.columnCount === 0) {
        console.log("No data found in the active sheet");
        return;
      }

      const formulas = usedRange.formulas;
      const changes = [];

      for (let i = 0; i < formulas.length; i++) {
        for (let j = 0; j < formulas[i].length; j++) {
          const formula = formulas[i][j];
          if (!isExcelFormula(formula)) {
            continue;
          }

          const newFormula = applyNamespaceReplacements(formula, replacements);
          if (newFormula !== formula) {
            changes.push({ row: i, col: j, formula: newFormula });
            console.log(`Found formula to replace: ${formula} -> ${newFormula}`);
          }
        }
      }

      if (changes.length === 0) {
        console.log("No matching custom function formulas were found");
        return;
      }

      changes.forEach((change) => {
        const cell = usedRange.getCell(change.row, change.col);
        cell.formulas = [[change.formula]];
      });

      await context.sync();
      console.log(`Successfully renamed ${changes.length} formula(s) ${successLabel}`);
    });
  } catch (error) {
    console.error("Error renaming functions: " + error);
    throw error;
  }
};

const renameBurgissToPrivatei = async () => {
  await renameFormulasInActiveSheet(BURGISS_TO_PRIVATEI, "from burgiss to Privatei");
};

const renamePrivateiToBurgiss = async () => {
  await renameFormulasInActiveSheet(PRIVATEI_TO_BURGISS, "from Privatei to burgiss");
};

const getAccountDetails = () => {
  try {
    const baseService = new BaseService();
    const user = baseService.getUser();
    const profileId = baseService.getProfileId();
    const selectedProfileId = getStringFromStorage(ProfileSettings.ProfileId);

    const accountDetails = {
      user: {
        profile: user.profile || {},
        id_token: user.id_token ? "***" + user.id_token.slice(-10) : null, // Show only last 10 chars for security
        access_token: user.access_token ? "***" + user.access_token.slice(-10) : null,
        expires_at: user.expires_at ? new Date(user.expires_at * 1000).toLocaleString() : null,
        session_state: user.session_state || null,
      },
      profileId: profileId || "Not set",
      selectedProfileId: selectedProfileId || "Not set",
    };

    const profile = accountDetails.user.profile as any;

    console.log("=== Account Details ===");
    console.log("User Profile:", profile);
    console.log("User ID:", profile?.sub || profile?.oid || "N/A");
    console.log("Name:", profile?.name || "N/A");
    console.log("Email:", profile?.email || profile?.preferred_username || "N/A");
    console.log("Token Expires At:", accountDetails.user.expires_at || "N/A");
    console.log("Profile ID:", accountDetails.profileId);
    console.log("Selected Profile ID:", accountDetails.selectedProfileId);
    console.log("======================");

    return accountDetails;
  } catch (error) {
    console.error("Error retrieving account details: " + error);
    throw error;
  }
};

export {
  reCalculate,
  writeToSheet,
  activeSheetName,
  activeSheetProps,
  getAllWorkSheets,
  hashNameCalculate,
  renameBurgissToPrivatei,
  renamePrivateiToBurgiss,
  getAccountDetails,
};
